import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useFeedback, FeedbackType } from "@/hooks/useFeedback";
import { FeedbackTypeSelector } from "./FeedbackTypeSelector";
import { Upload, X, Image as ImageIcon, Info, Camera } from "lucide-react";
import { cn } from "@/lib/utils";

const getMessagePlaceholder = (type: FeedbackType): string => {
  switch (type) {
    case 'bug':
      return "1. J'ai cliqué sur / fait...\n2. Il s'est passé...\n3. J'attendais que...";
    case 'suggestion':
      return "Actuellement : [décrivez le problème]\n\nMa proposition : [votre idée]";
    case 'question':
      return "J'aimerais savoir comment...";
    default:
      return "Décrivez votre retour...";
  }
};

const getTips = (type: FeedbackType): string => {
  switch (type) {
    case 'bug':
      return "Décrivez les étapes pour reproduire le problème et ce que vous attendiez.";
    case 'suggestion':
      return "Expliquez le problème actuel et votre idée d'amélioration.";
    case 'question':
      return "Donnez le contexte nécessaire pour qu'on puisse vous aider.";
    default:
      return "Soyez le plus précis possible pour qu'on puisse agir rapidement.";
  }
};

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const { submitFeedback, isSubmitting } = useFeedback();
  const [type, setType] = useState<FeedbackType>('suggestion');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setType('suggestion');
    setSubject('');
    setMessage('');
    setScreenshot(null);
    setPreviewUrl(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const success = await submitFeedback({
      type,
      subject,
      message,
      screenshot: screenshot || undefined,
    });

    if (success) {
      resetForm();
      onOpenChange(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        return;
      }
      
      setScreenshot(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isValid = message.trim().length >= 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Donner un feedback</DialogTitle>
          <DialogDescription>
            Aidez-nous à améliorer Piktable en partageant vos retours
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type selector */}
          <div className="space-y-2">
            <Label>Type de retour</Label>
            <FeedbackTypeSelector value={type} onChange={setType} />
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="subject">Sujet (optionnel)</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Ex: Bouton de confirmation ne répond pas"
              maxLength={100}
            />
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Description</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={getMessagePlaceholder(type)}
              className="min-h-[120px]"
            />
            <p className="text-xs text-muted-foreground flex items-start gap-1.5">
              <Info className="h-3 w-3 mt-0.5 shrink-0" />
              <span>{getTips(type)} Mentionnez la page concernée si pertinent.</span>
            </p>
          </div>

          {/* Screenshot upload */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Capture d'écran (optionnel)</Label>
              {type === 'bug' && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Camera className="h-3 w-3" />
                  Une capture nous aide beaucoup !
                </span>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            
            {!screenshot ? (
              <Button
                type="button"
                variant="outline"
                className="w-full h-20 border-dashed"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="flex flex-col items-center gap-1 text-muted-foreground">
                  <Upload className="h-5 w-5" />
                  <span className="text-sm">Ajouter une image</span>
                </div>
              </Button>
            ) : (
              <div className="relative rounded-lg border overflow-hidden">
                <img
                  src={previewUrl || ''}
                  alt="Screenshot preview"
                  className="w-full h-32 object-cover"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-6 w-6"
                  onClick={removeScreenshot}
                >
                  <X className="h-3 w-3" />
                </Button>
                <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-background/80 backdrop-blur-sm px-2 py-1 rounded text-xs">
                  <ImageIcon className="h-3 w-3" />
                  {screenshot.name}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={!isValid || isSubmitting}>
              {isSubmitting ? "Envoi..." : "Envoyer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
